import { useLayoutEffect, type ReactNode } from 'react';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';

/**
 * Validates that waitForTrigger was called for triggered messages.
 * This is a fail-safe to catch developer errors where they forget to use waitForTrigger.
 *
 * If waitForTrigger was not called:
 * - Logs an error in development
 * - Returns null to prevent rendering
 *
 * If waitForTrigger was called:
 * - Renders children (which are already wrapped with choreography by waitForTrigger)
 */
export const TriggeredMessageValidator = ({
	children,
	waitForTrigger,
}: {
	children: ReactNode;
	waitForTrigger?: WaitForTriggerFunction;
}) => {
	useLayoutEffect(() => {
		// Extract the ref from the function
		const calledRef = waitForTrigger?.__calledRef;

		// After render, check if waitForTrigger was called
		if (calledRef && !calledRef.current) {
			if (process.env.NODE_ENV !== 'production') {
				// eslint-disable-next-line no-console
				console.error(
					'[Post Office] Triggered message rendered without calling waitForTrigger. ' +
						'This message is client-triggered but the component did not call waitForTrigger. ' +
						'Please wrap your content: return waitForTrigger(condition, <YourContent />, onShow);',
				);
			} else {
				// eslint-disable-next-line no-console -- Temporary debug logging for trigger choreography
				console.info('[Post Office] Triggered message rendered without calling waitForTrigger.');
			}
		}
	}, [waitForTrigger]);

	// Extract ref for fail-safe check
	const calledRef = waitForTrigger?.__calledRef;

	// Fail-safe: Don't render if waitForTrigger wasn't called
	if (calledRef && !calledRef.current) {
		// eslint-disable-next-line no-console -- Temporary debug logging for trigger choreography
		console.info('[Post Office] Fail safe: do not render if waitForTrigger was not called');
		return null;
	}

	// If waitForTrigger was called, render children (which are already wrapped)
	// eslint-disable-next-line no-console -- Temporary debug logging for trigger choreography
	console.info('[Post Office] waitForTrigger was called, rendering children');
	return <>{children}</>;
};
